<template>
  <v-container>
    <v-card>
      <v-card-title class="headline">Add Profiling Questions</v-card-title>
      <v-card-text>
        <!-- General Info -->
        <v-card outlined class="mb-6 pa-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field label="Name" v-model="form.name" required />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.category"
                :items="['Media', 'Lifestyle', 'Brand', 'Banking']"
                label="Category"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-if="form.category && imageOptions[form.category]"
                v-model="form.image"
                :items="imageOptions[form.category]"
                label="Image URL"
                required
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-avatar>
                      <v-img :src="item"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>

                <template v-slot:selection="{ item }">
                  <v-chip small>
                    <v-avatar left>
                      <v-img :src="item"></v-img>
                    </v-avatar>
                    {{ item }}
                  </v-chip>
                </template>
              </v-select>

              <!-- fallback text field if no category selected -->
              <v-text-field
                v-else
                label="Image URL"
                v-model="form.image"
                required
              />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field
                label="Points"
                v-model="form.points"
                type="number"
                required
              />
            </v-col>
            <v-col cols="6" md="3" class="d-flex align-center">
              <v-switch v-model="form.isActive" label="Active" color="green" />
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-model="form.countries"
                :items="availableCountries"
                label="Target Countries"
                multiple
                chips
              />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="questionCount"
                :items="[1, 2, 3]"
                label="Number of Questions"
                @change="handleQuestionCountChange"
                required
              />
            </v-col>
          </v-row>
        </v-card>

        <!-- Question Panels -->
        <v-expansion-panels accordion>
          <v-expansion-panel
            v-for="(question, qIndex) in form.questions"
            :key="qIndex"
          >
            <v-expansion-panel-header>
              Question {{ qIndex + 1 }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="question.questionType"
                    :items="questionTypes"
                    label="Question Type"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-if="question.questionType === 'textbox'"
                    v-model="question.numFields"
                    label="Num of text fields"
                    type="number"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="Profiling Key"
                    v-model="question.profilingKey"
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-switch
                    v-model="question.isTimeSeries"
                    label="Is Time Series?"
                    color="primary"
                  />
                </v-col>

                <!-- Multilingual Question -->
                <v-col
                  cols="12"
                  v-for="(text, lang) in question.question.translations"
                  :key="lang"
                >
                  <v-text-field
                    :label="`Question Text (${lang.toUpperCase()})`"
                    v-model="question.question.translations[lang]"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    label="Add Language (e.g. ma, id)"
                    v-model="question.newLanguage"
                    @keyup.enter="addLanguage(qIndex)"
                  />
                </v-col>

                <!-- Show If Condition -->
                <v-col cols="12">
                  <v-switch
                    v-model="question.hasShowIf"
                    label="Display Logic?"
                    color="purple"
                  />
                </v-col>

                <v-col cols="12" v-if="question.hasShowIf">
                  <v-card outlined class="pa-4">
                    <h4 class="subtitle-1 mb-3">Display if:</h4>
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-select
                          :items="profilingData"
                          item-text="name"
                          item-value="_id"
                          label="Select Survey"
                          v-model="question.showIf.surveyId"
                          @change="(val) => getProfilingKey(qIndex, val)"
                          required
                        />
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-select
                          v-if="profilingKeysPerQuestion[qIndex]?.length"
                          :items="profilingKeysPerQuestion[qIndex]"
                          item-text="profilingKey"
                          item-value="profilingKey"
                          label="Profiling Key"
                          v-model="question.showIf.profilingKey"
                        />
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-select
                          v-if="getSelectedOptions(qIndex).length"
                          :items="getSelectedOptions(qIndex)"
                          item-text="value"
                          item-value="value"
                          label="Expected Answer"
                          v-model="question.showIf.answer"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>

                <!-- Options -->
                <v-col cols="12">
                  <h4 class="subtitle-1">Answer Options</h4>
                  <div
                    v-for="(option, oIndex) in question.options"
                    :key="oIndex"
                    class="mb-4"
                  >
                    <v-card flat outlined>
                      <v-card-title>
                        Option {{ oIndex + 1 }}
                        <v-spacer></v-spacer>
                        <v-btn icon @click="removeOption(qIndex, oIndex)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text>
                        <v-text-field label="Value" v-model="option.value" />
                        <v-row>
                          <v-col
                            cols="12"
                            v-for="(text, lang) in option.text.translations"
                            :key="lang"
                          >
                            <v-text-field
                              :label="`Text (${lang.toUpperCase()})`"
                              v-model="option.text.translations[lang]"
                            />
                          </v-col>
                        </v-row>
                        <v-text-field
                          label="Add Language"
                          v-model="option.newLanguage"
                          @keyup.enter="addOptionLanguage(qIndex, oIndex)"
                        />
                      </v-card-text>
                    </v-card>
                  </div>
                  <v-btn color="primary" small @click="addOption(qIndex)">
                    Add Option
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="success" @click="submit">Submit</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Preview -->
    <v-card v-if="form.questions.length" class="mt-6" outlined>
      <v-card-title>Mobile Preview</v-card-title>
      <v-card-text>
        <div
          v-for="(question, qIndex) in form.questions"
          :key="qIndex"
          class="mb-6"
        >
          <strong>Question {{ qIndex + 1 }}:</strong>
          <div>{{ question.question.translations.en || "No text" }}</div>

          <v-card
            v-for="(option, oIndex) in question.options"
            :key="oIndex"
            class="my-2 pa-2"
            outlined
          >
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2"
                >mdi-radiobox-blank</v-icon
              >
              {{ option.text.translations.en || "No text" }}
            </div>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2";
const service = new RestResource();

export default {
  name: "AddProfilingQuestion",
  data() {
    return {
      form: {
        name: "",
        points: 0,
        image: "",
        category: "",
        countries: [],
        questions: [],
        isActive: true,
        showIf: {
          surveyId: "",
          profilingKey: "",
          answer: "",
        },
      },
      hasShowIf: false,
      profilingData: [],
      profilingKeysPerQuestion: [],
      selectedProfilingId: null,
      profilingKeys: [],
      selectedOption: null,
      selectedProfilingKey: null,
      questionCount: 1,
      availableCountries: ["Malaysia", "Indonesia", "Singapore", "Thailand"],
      questionTypes: ["radio", "checkbox", "textbox"],
      imageOptions: {
        Media: [
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491180/profiling-questions/media_5_oed0sz.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491180/profiling-questions/media_2_ckbz8q.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491179/profiling-questions/media_3_qvwdhk.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491179/profiling-questions/media_4_llvbc8.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491179/profiling-questions/media_1_vnlwwx.jpg",
        ],
        Lifestyle: [
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491503/profiling-questions/lifestyle_4_suxpbl.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491503/profiling-questions/lifestyle_5_nhld93.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491503/profiling-questions/lifestyle_3_rxhzju.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491502/profiling-questions/lifestyle_2_y5ljha.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491502/profiling-questions/lifestyle_1_tpwxgr.jpg",
        ],
        Brand: [
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491802/profiling-questions/brand_3_neywuu.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491802/profiling-questions/brand_5_q38oir.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491802/profiling-questions/brand_1_qypdrk.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491802/profiling-questions/brand_4_mqnlnz.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491801/profiling-questions/brand_2_bfghhc.jpg",
        ],
        Banking: [
          "https://res.cloudinary.com/insightzclub/image/upload/v1755502986/profiling-questions/banking_4_m1llid.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755502985/profiling-questions/banking_2_shhmpm.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755502985/profiling-questions/banking_3_ouumgq.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755502984/profiling-questions/banking_1_dxwa8s.jpg",
          "https://res.cloudinary.com/insightzclub/image/upload/v1755491802/profiling-questions/brand_3_neywuu.jpg",
        ],
      },
    };
  },
  created() {
    this.initializeQuestions();
  },

  mounted() {
    this.fetchProfilingIds();
  },

  computed: {
    selectedOptions() {
      const selected = this.profilingKeys.find(
        (item) => item.profilingKey === this.selectedProfilingKey
      );
      return selected ? selected.options : [];
    },
  },

  methods: {
    async fetchProfilingIds() {
      try {
        const response = await service.getAllProfilingIds();
        this.profilingData = response.data.surveyIds;
      } catch (error) {
        console.error("Error fetching profiling IDs:", error);
      }
    },

    async getProfilingKey(qIndex, surveyId) {
      if (!surveyId) {
        alert("Please select a question");
        return;
      }

      try {
        this.$setLoader();
        const response = await service.getProfilingKey({
          surveyId: surveyId,
        });

        const keys = response.data.key.flatMap((item) =>
          item.questions.map((q) => ({
            profilingKey: q.profilingKey,
            options: q.options || [],
          }))
        );

        this.$set(this.profilingKeysPerQuestion, qIndex, keys);
      } catch (error) {
        console.error("Error fetching profiling key:", error);
      } finally {
        this.$disableLoader();
      }
    },

    getSelectedOptions(qIndex) {
      const keys = this.profilingKeysPerQuestion[qIndex] || [];
      const selectedKey = this.form.questions[qIndex].showIf.profilingKey;
      const keyObj = keys.find((k) => k.profilingKey === selectedKey);
      return keyObj ? keyObj.options : [];
    },

    initializeQuestions() {
      this.form.questions = Array.from({ length: this.questionCount }, () =>
        this.newQuestion()
      );
    },
    handleQuestionCountChange() {
      const currentLength = this.form.questions.length;
      if (this.questionCount > currentLength) {
        for (let i = currentLength; i < this.questionCount; i++) {
          this.form.questions.push(this.newQuestion());
        }
      } else {
        this.form.questions.splice(this.questionCount);
      }
    },
    newQuestion() {
      return {
        questionType: "",
        profilingKey: "",
        isTimeSeries: false,
        question: {
          translations: {
            en: "",
            ma: "",
          },
        },
        options: [],
        newLanguage: "",
        showIf: {
          surveyId: "",
          profilingKey: "",
          answer: "",
        },
      };
    },
    addLanguage(qIndex) {
      const question = this.form.questions[qIndex];
      if (
        question.newLanguage &&
        !question.question.translations[question.newLanguage]
      ) {
        this.$set(question.question.translations, question.newLanguage, "");
        question.newLanguage = "";
      }
    },
    addOption(qIndex) {
      this.form.questions[qIndex].options.push({
        value: "",
        text: {
          translations: {
            en: "",
            ma: "",
          },
        },
        newLanguage: "",
      });
    },
    addOptionLanguage(qIndex, oIndex) {
      const option = this.form.questions[qIndex].options[oIndex];
      if (option.newLanguage && !option.text.translations[option.newLanguage]) {
        this.$set(option.text.translations, option.newLanguage, "");
        option.newLanguage = "";
      }
    },
    removeOption(qIndex, oIndex) {
      this.form.questions[qIndex].options.splice(oIndex, 1);
    },
    async submit() {
      try {
        const dataToSend = {
          ...this.form,
          questions: this.form.questions.map((q) => {
            // remove empty question translations
            const filteredQuestionTranslations = Object.fromEntries(
              Object.entries(q.question.translations).filter(
                ([, text]) => text && text.trim() !== ""
              )
            );

            return {
              questionType: q.questionType,
              profilingKey: q.profilingKey,
              isTimeSeries: q.isTimeSeries,
              question: {
                translations: filteredQuestionTranslations,
              },
              options: q.options.map((o) => {
                // remove empty option translations
                const filteredOptionTranslations = Object.fromEntries(
                  Object.entries(o.text.translations).filter(
                    ([, text]) => text && text.trim() !== ""
                  )
                );

                return {
                  value: o.value,
                  text: {
                    translations: filteredOptionTranslations,
                  },
                };
              }),
              showIf: q.hasShowIf ? q.showIf : null,
            };
          }),
        };

        this.$setLoader();
        await service.addProfilingQuestion(dataToSend);
        this.$emit("saved");
        this.$router.push({ name: "MobileV2ProfilingQuestion" });
      } catch (err) {
        console.error(err);
        alert("Failed to save question");
      } finally {
        this.$disableLoader();
      }
    },
  },
};
</script>
